import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useEvent } from 'react-use-event-hook'
import { Avatar } from '@components/Avatar/Avatar'
import { AvatarTextHorizontal } from '@components/Avatar/AvatarTextHorizontal'
import { BotReviewsSection } from '@components/BotReviews/BotReviewsSection'
import { Box, Button, Heading, Icon, IconButton, Paragraph, Stack, Text } from '@ui'
import useCopyToClipboard from 'hooks/useCopyToClipboard'
import { useDevice } from 'hooks/useDevice'
import { useMobile } from 'hooks/useMobile'
import linkifyit from 'linkify-it'
import { ToneName } from 'ui/styles/themes'
import { vars } from 'ui/styles/vars.css'
import { formatUrlWithoutProtocol, getBotShareUrl } from 'ui/utils/utils'
import type { BotData } from 'use-towns-client'
import { Address, useGetRootKeyFromLinkedWallet, useUserLookupStore } from 'use-towns-client'

import { InstallBotInfoSection } from './InstallBotInfoSection'

type InstallBotLayoutProps = {
    bottomContent?: React.ReactNode
    isPreview: boolean
    bot: BotData
    onBack?: () => void
}

export const InstallBotLayout = (props: InstallBotLayoutProps) => {
    const { bot, isPreview } = props

    const { data: userId } = useGetRootKeyFromLinkedWallet({ walletAddress: bot.owner })
    const [copiedLink, setCopiedLink] = useState(false)
    const [, copy] = useCopyToClipboard()

    useEffect(() => {
        if (copiedLink) {
            const timeout = setTimeout(() => {
                setCopiedLink(false)
            }, 1000)
            return () => {
                clearTimeout(timeout)
            }
        }
    }, [copiedLink])

    const onCopyBotShareLink = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault()

            const botShareUrl = getBotShareUrl({ appAddress: bot.appAddress })
            const asyncCopy = async () => {
                const copied = await copy(botShareUrl)
                setCopiedLink(copied)
            }
            asyncCopy()
        },
        [bot.appAddress, copy],
    )

    const { isTouch } = useDevice()

    return (
        <BotPageSkeleton
            backgroundImage={bot.metadata?.avatarUrl}
            footerContent={props.bottomContent}
            onBack={props.onBack}
        >
            <TwoColumnsLayout
                mobileHeader={
                    isTouch ? (
                        <Stack alignItems="center" paddingTop="x4">
                            <Box width="x20" height="x20" rounded="full">
                                <Avatar
                                    isBot
                                    size="avatar_100"
                                    src={bot.metadata?.avatarUrl}
                                    imageVariant="thumbnail300"
                                />
                            </Box>
                        </Stack>
                    ) : undefined
                }
                leftContent={
                    <Stack gap="x6" paddingTop={{ desktop: 'x4', mobile: 'x2' }}>
                        <Stack horizontal gap="sm" alignContent="start">
                            <Header
                                name={bot.metadata?.displayName}
                                owner={bot.owner}
                                motto={bot.metadata?.motto}
                                appAddress={bot.appAddress}
                                userId={userId}
                                isPreview={isPreview}
                            />
                        </Stack>
                        <InstallBotInfoSection appAddress={bot.appAddress} />
                        <Stack gap="x4">
                            <BotDescription description={bot.metadata?.description} />
                            {bot.metadata?.externalUrl && (
                                <BotExternalLink link={bot.metadata?.externalUrl} />
                            )}
                        </Stack>
                        <BotReviewsSection showSummary appAddress={bot.appAddress} />
                        <Box height="x12" shrink={false} />
                    </Stack>
                }
                rightContent={
                    !isTouch ? (
                        <Stack gap="lg" alignItems="center" paddingTop="x4">
                            <Box padding width="100%" aspectRatio="1/1">
                                <Avatar
                                    isBot
                                    size={{ desktop: 'avatar_100', mobile: 'avatar_lg' }}
                                    src={bot.metadata?.avatarUrl}
                                    imageVariant="thumbnail300"
                                />
                            </Box>

                            <Box tooltip="Copy link">
                                <Button
                                    size="button_md"
                                    width="300"
                                    rounded="full"
                                    tone="lightHover"
                                    color="default"
                                    data-testid="town-bot-install-share-link-button"
                                    onClick={onCopyBotShareLink}
                                >
                                    {copiedLink ? (
                                        'Link copied'
                                    ) : (
                                        <>
                                            <Icon type="share" size="square_sm" />
                                            Share Link
                                        </>
                                    )}
                                </Button>
                            </Box>
                        </Stack>
                    ) : null
                }
            />
        </BotPageSkeleton>
    )
}

type User = {
    ensName?: string
}

const Header = (props: {
    name?: string
    owner?: string
    userId?: string
    motto?: string
    appAddress: Address
    isPreview?: boolean
}) => {
    const { name, owner, userId, appAddress, isPreview, motto } = props
    const { isTouch } = useDevice()
    const { lookupUser } = useUserLookupStore(
        (state: { lookupUser: (userId: Address) => User | undefined }) => ({
            lookupUser: state.lookupUser,
        }),
    )

    const ownerUser = useMemo(() => {
        if (!userId) {
            return undefined
        }
        return lookupUser(userId as Address)
    }, [userId, lookupUser])

    const onTouchSharePressed = useEvent(async () => {
        const url = getBotShareUrl({ appAddress })
        try {
            await navigator.share({ title: name, url: url })
        } catch (e) {
            console.error(e)
        }
    })

    return (
        <Stack horizontal width="100%">
            <Stack
                justifyContent={{
                    mobile: 'center',
                }}
                gap={{
                    desktop: 'md',
                    mobile: 'sm',
                }}
                paddingTop={isTouch ? 'sm' : 'none'}
            >
                <Stack gap>
                    <Box gap display="flex" flexDirection="row" alignItems="center">
                        <Heading level={2} style={{ textTransform: 'none' }}>
                            {name}
                        </Heading>
                        <Icon type="robot" size="square_md" />
                    </Box>
                </Stack>
                <Stack gap="sm">
                    {motto && <Text>{motto}</Text>}
                    <Stack horizontal justifySelf="end" height="x4">
                        {userId && (
                            <AvatarTextHorizontal
                                userId={userId}
                                abstractAccountaddress={owner as Address}
                                prepend={<Text color="gray2">By </Text>}
                                customDisplayName={ownerUser?.ensName}
                            />
                        )}
                    </Stack>
                </Stack>
            </Stack>

            {isTouch && !isPreview && (
                <Box grow justifyContent="end" alignItems="end">
                    <Box width="x4" height="x4" rounded="sm">
                        <IconButton
                            centerContent
                            icon="share"
                            background="lightHover"
                            color="default"
                            aspectRatio="1/1"
                            onClick={onTouchSharePressed}
                        />
                    </Box>
                </Box>
            )}
        </Stack>
    )
}

const TwoColumnsLayout = (props: {
    leftContent: React.ReactNode
    rightContent: React.ReactNode
    mobileHeader?: React.ReactNode
}) => {
    const isMobile = useMobile()

    if (isMobile) {
        return (
            <Stack scroll>
                {props.mobileHeader}
                {props.leftContent}
            </Stack>
        )
    }

    return (
        <Stack horizontal gap="x4" alignItems="start" justifyContent="spaceBetween">
            <Stack grow gap="x4" style={{ maxWidth: 650 }}>
                {props.leftContent}
            </Stack>
            <Stack gap="lg" position="sticky" top="none" paddingTop="x4" width="300">
                {props.rightContent}
            </Stack>
        </Stack>
    )
}

const BotPageSkeleton = (props: {
    onBack?: () => void
    footerContent?: React.ReactNode
    backgroundImage?: string
    children?: React.ReactNode
}) => {
    // const PAGE_MAX_WIDTH = 1050
    const isMobile = useMobile()

    if (isMobile) {
        return (
            <Stack grow paddingX="sm" gap="x4" position="relative">
                {props.backgroundImage && (
                    <Box
                        absoluteFill
                        style={{
                            backgroundImage: `url(${props.backgroundImage})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            filter: 'blur(100px)',
                            opacity: 0.3,
                            pointerEvents: 'none',
                        }}
                    />
                )}

                <Box absoluteFill paddingX overflow="scroll">
                    {props.children}
                    <Box minHeight="x6" />
                </Box>
                {props.footerContent}
            </Stack>
        )
    }

    return (
        <Box grow horizontal paddingX position="relative">
            {props.backgroundImage && (
                <Box
                    absoluteFill
                    style={{
                        backgroundImage: `url(${props.backgroundImage})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        filter: 'blur(100px)',
                        opacity: 0.3,
                        pointerEvents: 'none',
                    }}
                />
            )}

            <Box grow absoluteFill paddingX alignItems="center">
                <Box scroll maxWidth="1000" width="100%">
                    <Box minHeight="x12" />
                    {props.children}
                    <Box minHeight="x12" />
                </Box>
                {props.footerContent}
                <Box
                    grow
                    height="height_md"
                    alignItems="start"
                    position="absolute"
                    left="none"
                    top="none"
                    padding="x2"
                >
                    <Box minWidth="x4" position="relative" left="none" top="none" zIndex="above">
                        {props.onBack && (
                            <IconButton
                                cursor="pointer"
                                size="square_md"
                                background="lightHover"
                                icon="arrowLeft"
                                onClick={props.onBack}
                            />
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}

const linkify = linkifyit()
const BOT_DESCRIPTION_MAX_LENGTH = 350
const BotDescription = (props: { description?: string }) => {
    const { description = '' } = props
    const { isTouch } = useDevice()
    const [isExpanded, setIsExpanded] = useState<boolean>(false)
    const shortenedDescription = useMemo(() => {
        if (isExpanded) {
            return description
        }
        if (description.length > BOT_DESCRIPTION_MAX_LENGTH) {
            return description.slice(0, BOT_DESCRIPTION_MAX_LENGTH) + '…'
        }
        return description
    }, [description, isExpanded])

    const renderDescriptionWithLinks = (text: string) => {
        const matches = linkify.match(text)
        if (!matches) {
            return text
        }

        const result = []
        let lastIndex = 0

        matches.forEach((match, idx) => {
            if (match.index > lastIndex) {
                result.push(text.substring(lastIndex, match.index))
            }

            result.push(
                <Box
                    key={`link-${match.url}-${match.index}`}
                    as="a"
                    href={match.url}
                    target="_blank"
                    color="cta2"
                >
                    {match.text}
                </Box>,
            )

            lastIndex = match.lastIndex
        })

        if (lastIndex < text.length) {
            result.push(text.substring(lastIndex))
        }

        return result
    }

    const canExpand = description && description.length > BOT_DESCRIPTION_MAX_LENGTH && !isExpanded

    if (description.length === 0) {
        return null
    }

    return (
        <Stack gap>
            <Text strong size="md">
                About
            </Text>
            <Box display="inline-block" style={{ maxWidth: isTouch ? '100%' : '55ch' }}>
                <Paragraph size="lg" style={{ whiteSpace: 'pre-line' }}>
                    {renderDescriptionWithLinks(shortenedDescription)}
                    {canExpand && (
                        <span
                            style={{ color: vars.color.tone[ToneName.Accent], cursor: 'pointer' }}
                            tabIndex={0}
                            role="button"
                            aria-expanded={isExpanded}
                            onClick={() => setIsExpanded(true)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault()
                                    setIsExpanded(true)
                                }
                            }}
                        >
                            {' '}
                            more
                        </span>
                    )}
                </Paragraph>
            </Box>
        </Stack>
    )
}

const BotExternalLink = (props: { link: string }) => {
    const { link } = props
    return (
        <Stack gap>
            <Text strong size="md">
                External Link
            </Text>
            <Stack
                horizontal
                hoverable
                gap="sm"
                alignItems="center"
                padding="none"
                rounded="sm"
                alignSelf="start"
                color="gray1"
            >
                <Icon type="link" size="square_xs" />
                <Box as="a" href={link} target="_blank" rel="noopener noreferrer">
                    {formatUrlWithoutProtocol(link)?.replace(/\/$/, '')}
                </Box>
            </Stack>
        </Stack>
    )
}
